import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import {
  BUFFER_HOME_TASK_STATUS,
  GetBufferHomeChecklist,
} from './useBufferHomeChecklist'
import type { BufferHomeTaskKey } from './useBufferHomeChecklist'

export const DismissBufferHomeTask = graphql(/* GraphQL */ `
  mutation DismissBufferHomeTask($input: DismissBufferHomeTaskInput!) {
    dismissBufferHomeTask(input: $input) {
      ... on DismissBufferHomeTaskSuccess {
        bufferHomeChecklist {
          tasks {
            taskKey
            status
          }
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UseDismissBufferHomeTaskResult = {
  dismiss: (taskKey: BufferHomeTaskKey) => void
}

/**
 * Mutation hook for dismissing a Buffer Home first-steps task (e.g. "I work
 * alone" on the Invite Team card). On success, writes the updated checklist
 * back to the `GetBufferHomeChecklist` cache entry and fires the `Buffer
 * Home Task State Changed` tracking event with the status the task had
 * before the mutation.
 */
export const useDismissBufferHomeTask = (): UseDismissBufferHomeTaskResult => {
  const organizationId = useOrganizationId()
  const [dismissTask] = useMutation(DismissBufferHomeTask)

  const dismiss = useCallback(
    (taskKey: BufferHomeTaskKey) => {
      if (!organizationId) return

      void dismissTask({
        variables: { input: { organizationId, taskKey } },
        update: (cache, { data }) => {
          const payload = data?.dismissBufferHomeTask
          if (
            !payload ||
            payload.__typename !== 'DismissBufferHomeTaskSuccess'
          ) {
            return
          }

          // The cache still holds the pre-mutation checklist until the
          // write below, so this is the task's previous status.
          const previous = cache.readQuery({
            query: GetBufferHomeChecklist,
            variables: { input: { organizationId } },
          })
          const previousStatus =
            previous?.bufferHomeChecklist.tasks.find(
              (task) => task.taskKey === taskKey,
            )?.status ?? BUFFER_HOME_TASK_STATUS.Pending

          cache.writeQuery({
            query: GetBufferHomeChecklist,
            variables: { input: { organizationId } },
            data: {
              bufferHomeChecklist: payload.bufferHomeChecklist,
            },
          })

          BufferTracker.bufferHomeTaskStateChanged({
            organizationId,
            clientName: Client.PublishWeb,
            taskKey,
            previousStatus,
            newStatus: BUFFER_HOME_TASK_STATUS.Dismissed,
          })
        },
      })
    },
    [dismissTask, organizationId],
  )

  return { dismiss }
}
